import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

import useSharedStyles from './theme/pbdqShared.styles';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import PbdqArrowRightIcon from './components/PbdqArrowRightIcon.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';

import heroImg from '../../assets/images/PoweredByDQ/ProgramValidationHero.png';

/*
 * Program Validation — rebuilt to match the design, styling and content
 * of the mockup HTML ("Powered by DQ - Website.html") and the approved
 * visual system.
 */

const MOBILE = '@media (max-width: 820px)';

const useStyles = makeStyles(() => ({
    accent: { color: '#ED6628' },

    // ── SECTION 0: HERO ────────────────────────────────────────────────────────
    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
        overflow: 'hidden',
    },
    heroContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(40px, 5vw, 64px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '28px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    heroTextCol: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 20,
        alignContent: 'start',
    },
    heroTitle: {
        fontSize: 'clamp(36px, 4.2vw, 60px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '16ch',
        color: '#1B2430',
    },
    heroLead: {
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
    },
    heroCtas: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
    },
    btnHeroPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    btnHeroSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#1B2430',
        border: '1px solid #D5D9E0',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'border-color 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            borderColor: '#1B2430',
            color: '#1B2430',
            textDecoration: 'none',
        },
    },
    heroArtCol: {
        flex: '1 1 400px',
        minWidth: 0,
        maxWidth: 640,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        [MOBILE]: {
            minHeight: 0,
            maxWidth: 'none',
            paddingBottom: 0,
        },
    },
    heroImg: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },

    // ── COMMON SECTION WRAPPERS ───────────────────────────────────────────────
    sectionContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    sectionEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
    },
    sectionHeaderWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    sectionTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        width: '100%',
        color: '#1B2430',
    },
    sectionLead: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        width: '100%',
        color: '#5B6470',
    },

    // ── SECTION 1: TIERS ──────────────────────────────────────────────────────
    tiersSection: {
        background: '#FFFFFF',
    },
    tiersGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 40,
        alignItems: 'stretch',
    },
    tierCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 'clamp(24px, 2.6vw, 32px)',
        borderRadius: 24,
        position: 'relative',
        boxSizing: 'border-box',
    },
    tierCardTop: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    tierTag: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    tierFeaturedBadge: {
        fontSize: 12,
        fontWeight: 700,
        color: '#ED6628',
    },
    tierTitle: {
        fontSize: 'clamp(22px, 2vw, 26px)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    tierIntro: {
        fontSize: 15,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    tierPointsList: {
        listStyle: 'none',
        margin: '4px 0 0',
        padding: 0,
        display: 'grid',
        gap: 10,
    },
    tierPointItem: {
        display: 'grid',
        gridTemplateColumns: '18px 1fr',
        gap: 10,
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#1B2430',
    },
    tierBestFor: {
        fontSize: 14,
        lineHeight: 1.55,
        margin: 'auto 0 0',
        paddingTop: 16,
        borderTop: '1px solid #E6E9EE',
        color: '#5B6470',
    },
    tierCtaBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 48,
        padding: '0 20px',
        borderRadius: 999,
        textDecoration: 'none',
        fontSize: 14.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        border: '1px solid transparent',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1), border-color 200ms ease',
    },

    // ── SECTION 2: COMPARISON TABLE ───────────────────────────────────────────
    compareSection: {
        background: '#F4F6F9',
    },
    tableWrap: {
        overflowX: 'auto',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        background: '#FFFFFF',
    },
    tableInner: {
        minWidth: 640,
    },
    tableHeader: {
        display: 'grid',
        gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
        gap: 16,
        padding: '16px 24px',
        background: '#F8F9FB',
        borderBottom: '1px solid #E6E9EE',
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#6B7280',
    },
    tableRow: {
        display: 'grid',
        gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
        gap: 16,
        padding: '16px 24px',
        borderBottom: '1px solid #EEF1F4',
        fontSize: 14.5,
        alignItems: 'center',
        background: '#FFFFFF',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    tableNote: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px 32px',
        alignItems: 'baseline',
        marginTop: 20,
        padding: '18px 22px',
        borderRadius: 16,
        background: '#F4F6F9',
    },
    tableNoteLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#2A4057',
        whiteSpace: 'nowrap',
    },
    tableNoteText: {
        margin: 0,
        fontSize: 14.5,
        lineHeight: 1.55,
        color: '#5B6470',
        flex: '1 1 400px',
    },

    // ── SECTION 3: IMPACT & LIFECYCLE ─────────────────────────────────────────
    impactSection: {
        background: '#FFFFFF',
    },
    impactGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
    },
    impactCard: {
        padding: 'clamp(24px, 3vw, 40px)',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        display: 'grid',
        gap: 18,
        alignContent: 'start',
        background: '#FFFFFF',
        boxSizing: 'border-box',
    },
    cardTitle: {
        fontSize: 'clamp(24px, 2.4vw, 32px)',
        lineHeight: 1.12,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    cardLead: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    impactStepsList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 10,
    },
    impactStepItem: {
        display: 'grid',
        gridTemplateColumns: '32px 1fr',
        gap: 12,
        alignItems: 'center',
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#1B2430',
    },
    impactStepNum: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: '#FAEDDA',
        color: '#8A5A10',
        fontSize: 12,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
    },
    impactFootnote: {
        fontSize: 13.5,
        lineHeight: 1.55,
        margin: 0,
        paddingTop: 14,
        borderTop: '1px solid #E6E9EE',
        color: '#5B6470',
    },
    lifecycleCard: {
        padding: 'clamp(24px, 3vw, 40px)',
        borderRadius: 24,
        background: '#F4F6F9',
        display: 'grid',
        gap: 18,
        alignContent: 'start',
        boxSizing: 'border-box',
    },
    lifecycleWrap: {
        display: 'grid',
        gap: 0,
        borderTop: '1px solid #D9DEE5',
        marginTop: 6,
    },
    lifecycleRow: {
        display: 'grid',
        gridTemplateColumns: '40px 1fr',
        gap: 14,
        padding: '16px 0',
        borderBottom: '1px solid #D9DEE5',
        alignItems: 'start',
    },
    lifecycleNum: {
        fontSize: 18,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: '#ED6628',
        fontVariantNumeric: 'tabular-nums',
    },
    lifecycleText: {
        fontSize: 15,
        lineHeight: 1.55,
        color: '#1B2430',
    },

    // ── SECTION 4: COMMERCIAL SCOPE ───────────────────────────────────────────
    quotationSection: {
        background: '#FFF8F4',
    },
    quotationPanel: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(24px, 3vw, 48px)',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'clamp(24px, 3vw, 44px)',
        borderRadius: 24,
        background: '#172939',
        color: '#FFFFFF',
        boxSizing: 'border-box',
    },
    quotationLeft: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 16,
    },
    quotationPill: {
        display: 'inline-flex',
        width: 'max-content',
        maxWidth: '100%',
        alignItems: 'center',
        padding: '8px 14px',
        borderRadius: 999,
        background: 'rgba(255, 255, 255, 0.12)',
        fontSize: 12,
        lineHeight: 1.3,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#FFFFFF',
    },
    quotationTitle: {
        fontSize: 'clamp(24px, 2.6vw, 36px)',
        lineHeight: 1.1,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#FFFFFF',
    },
    quotationLead: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#D9E3EE',
    },
    quotationList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 8,
    },
    quotationItem: {
        display: 'grid',
        gridTemplateColumns: '18px 1fr',
        gap: 10,
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#FFFFFF',
    },
    quotationFootnote: {
        fontSize: 13.5,
        lineHeight: 1.55,
        margin: 0,
        color: '#8AA3BC',
    },
    btnQuotation: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 54,
        padding: '0 26px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 16,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
        },
    },

    // ── SECTION 5: NEXT STEP ──────────────────────────────────────────────────
    nextStepSection: {
        background: '#F4F6F9',
        borderTop: '1px solid #E9ECF0',
    },
    nextStepContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    nextStepTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '22ch',
        color: '#1B2430',
    },
    nextStepCtas: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
    },
    btnBottomDark: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#1B2430',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#2A3A4C',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    btnBottomWhite: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#1B2430',
        border: '1px solid #D5D9E0',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'border-color 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            borderColor: '#1B2430',
            color: '#1B2430',
            textDecoration: 'none',
        },
    },
}));

const TIERS = [
    {
        tag: 'Essential',
        featured: false,
        border: '#E6E9EE',
        bg: '#FFFFFF',
        tagBg: '#EEF3F8',
        tagColor: '#2A4057',
        ctaBg: '#FFFFFF',
        ctaColor: '#1B2430',
        ctaBorder: '#D5D9E0',
        h: 'Establish credible standards alignment.',
        intro: 'A focused independent review for programs that need to demonstrate how their design and intended outcomes align with recognized AI-digital literacy standards.',
        points: [
            'Independent DQ Stars alignment review',
            'Standards-alignment report',
            'Findings and priorities for improvement',
        ],
        best: 'Programs seeking credible alignment evidence before approaching buyers, funders or partners.',
        cta: 'Request Essential validation',
    },
    {
        tag: 'Standard',
        featured: true,
        border: '#ED6628',
        bg: '#FFF8F4',
        tagBg: '#ED6628',
        tagColor: '#FFFFFF',
        ctaBg: '#ED6628',
        ctaColor: '#FFFFFF',
        ctaBorder: '#ED6628',
        h: 'Validate alignment and measure initial impact.',
        intro: 'Standard adds a tailored assessment to the Essential review, enabling the provider to measure whether its program produces the intended participant outcomes.',
        points: [
            'Everything included in Essential',
            'Tailored impact-assessment creation',
            'Pre- and post-program measurement for up to 100 participants',
        ],
        best: 'Established programs that need independent alignment evidence and an initial demonstration of measurable impact.',
        cta: 'Request Standard validation',
    },
    {
        tag: 'Comprehensive',
        featured: false,
        border: '#E6E9EE',
        bg: '#FFFFFF',
        tagBg: '#EEF3F8',
        tagColor: '#2A4057',
        ctaBg: '#FFFFFF',
        ctaColor: '#1B2430',
        ctaBorder: '#D5D9E0',
        h: 'Build robust evidence for adoption and scale.',
        intro: 'Comprehensive adds deeper expert support and broader impact measurement for programs preparing for major procurement, investment or multi-market expansion.',
        points: [
            'Everything included in Standard',
            'Expert consulting on program quality, evidence and improvement',
            'Pre- and post-program measurement for up to 1,000 participants',
        ],
        best: 'Major providers, governments, funders and ecosystems requiring robust evidence for large-scale decisions.',
        cta: 'Request Comprehensive validation',
    },
];

const COMPARISON_ROWS = [
    { f: 'Independent alignment review', a: 'Included', b: 'Included', c: 'Included' },
    { f: 'Standards-alignment report', a: 'Included', b: 'Included', c: 'Included' },
    { f: 'Improvement findings', a: 'Included', b: 'Included', c: 'Detailed' },
    { f: 'Tailored assessment creation', a: '—', b: 'Included', c: 'Included' },
    { f: 'Pre- and post-impact measurement', a: '—', b: 'Included', c: 'Included' },
    { f: 'Participants included', a: '—', b: 'Up to 100', c: 'Up to 1,000' },
    { f: 'Expert consulting', a: '—', b: '—', c: 'Included' },
    { f: 'Best for', a: 'Alignment evidence', b: 'Initial impact evidence', c: 'Adoption and scale' },
];

const IMPACT_STEPS = [
    { num: '01', text: 'Establish participant capability before program delivery' },
    { num: '02', text: 'Reassess participants after completion using aligned indicators' },
    { num: '03', text: 'Report measured improvement, remaining gaps and priorities' },
];

const LIFECYCLE_STEPS = [
    { num: '01', text: 'Complete the initial review and measurement included in your selected tier' },
    { num: '02', text: 'Renew the validation annually' },
    { num: '03', text: 'Complete full revalidation every three years or following material program changes' },
];

const SCOPE_POINTS = [
    'Validation tier and number of programs',
    'Audiences, languages and assessment requirements',
    'Participant volume, consulting and reporting scope',
];

const CheckIcon = ({ color = '#ED6628' }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }}>
        <path d="m5 12 5 5L20 7" />
    </svg>
);

const PbdqProgramValidationPageContent = () => {
    const shared = useSharedStyles();
    const classes = useStyles();
    const openAgentChat = useAgentChat();

    const scrollToTiers = (e) => {
        if (e) e.preventDefault();
        const el = document.getElementById('tiers');
        if (el) {
            const y = el.getBoundingClientRect().top + window.pageYOffset - 16;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    return (
        <div className={shared.page} style={{ background: '#FFFFFF', color: '#1B2430' }}>
            <PbdqNav />

            {/* ── SECTION 0: HERO ────────────────────────────────────────── */}
            <section aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroContainer}>
                    <div className={classes.heroTextCol}>
                        <h1 className={classes.heroTitle}>
                            Validate quality. Prove impact. <span className={classes.accent}>Scale with confidence.</span>
                        </h1>
                        <p className={classes.heroLead}>
                            Begin with independent standards alignment. Add tailored impact measurement when you need evidence of participant outcomes. Select Comprehensive validation when your program requires deeper expert support and evidence for large-scale adoption.
                        </p>
                        <div className={classes.heroCtas}>
                            <a
                                href="#tiers"
                                onClick={scrollToTiers}
                                className={classes.btnHeroPrimary}
                            >
                                Choose your validation tier
                            </a>
                            <button
                                type="button"
                                onClick={openAgentChat}
                                className={classes.btnHeroSecondary}
                            >
                                Request a scoped quotation
                            </button>
                        </div>
                    </div>

                    <div className={classes.heroArtCol}>
                        <img
                            src={heroImg}
                            alt="Program Validation framework and standards alignment"
                            className={classes.heroImg}
                        />
                    </div>
                </div>
            </section>

            <main style={{ minWidth: 0 }}>
                {/* ── SECTION 1: CHOOSE YOUR VALIDATION TIER ───────────────── */}
                <section id="tiers" aria-label="Choose your validation tier" className={classes.tiersSection}>
                    <div className={classes.sectionContainer}>
                        <span className={classes.sectionEyebrow}>Choose your validation tier</span>
                        <div className={classes.sectionHeaderWrap}>
                            <h2 className={classes.sectionTitle}>
                                Select the level of evidence your program needs.
                            </h2>
                            <p className={classes.sectionLead}>
                                Begin with independent standards alignment. Add tailored impact measurement when you need evidence of participant outcomes. Choose Comprehensive when you require expert support and large-scale impact evidence.
                            </p>
                        </div>

                        <div className={classes.tiersGrid}>
                            {TIERS.map((tier) => (
                                <div
                                    key={tier.tag}
                                    className={classes.tierCard}
                                    style={{
                                        border: `1px solid ${tier.border}`,
                                        background: tier.bg,
                                    }}
                                >
                                    <div className={classes.tierCardTop}>
                                        <span
                                            className={classes.tierTag}
                                            style={{
                                                background: tier.tagBg,
                                                color: tier.tagColor,
                                            }}
                                        >
                                            {tier.tag}
                                        </span>
                                        {tier.featured && (
                                            <span className={classes.tierFeaturedBadge}>Most chosen</span>
                                        )}
                                    </div>
                                    <h3 className={classes.tierTitle}>{tier.h}</h3>
                                    <p className={classes.tierIntro}>{tier.intro}</p>
                                    <ul className={classes.tierPointsList}>
                                        {tier.points.map((p) => (
                                            <li key={p} className={classes.tierPointItem}>
                                                <CheckIcon color="#ED6628" />
                                                <span>{p}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className={classes.tierBestFor}>
                                        <strong style={{ color: '#1B2430' }}>Best for:</strong> {tier.best}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={openAgentChat}
                                        className={classes.tierCtaBtn}
                                        style={{
                                            background: tier.ctaBg,
                                            color: tier.ctaColor,
                                            borderColor: tier.ctaBorder,
                                        }}
                                    >
                                        {tier.cta}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── SECTION 2: COMPARE THE THREE TIERS ───────────────────── */}
                <section aria-label="Compare the three tiers" className={classes.compareSection}>
                    <div className={classes.sectionContainer}>
                        <span className={classes.sectionEyebrow} style={{ marginBottom: 20 }}>
                            Compare the three tiers
                        </span>
                        <div className={classes.tableWrap}>
                            <div className={classes.tableInner}>
                                <div className={classes.tableHeader}>
                                    <span>Feature</span>
                                    <span>Essential</span>
                                    <span style={{ color: '#ED6628' }}>Standard</span>
                                    <span>Comprehensive</span>
                                </div>
                                {COMPARISON_ROWS.map((row) => (
                                    <div key={row.f} className={classes.tableRow}>
                                        <span style={{ fontWeight: 600 }}>{row.f}</span>
                                        <span style={{ color: row.a === '—' ? '#8AA3BC' : '#1B2430' }}>
                                            {row.a}
                                        </span>
                                        <span style={{ color: row.b === '—' ? '#8AA3BC' : '#1B2430', fontWeight: 600 }}>
                                            {row.b}
                                        </span>
                                        <span style={{ color: row.c === '—' ? '#8AA3BC' : '#1B2430' }}>
                                            {row.c}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={classes.tableNote}>
                            <span className={classes.tableNoteLabel}>
                                Additional participant measurement
                            </span>
                            <p className={classes.tableNoteText}>
                                <strong style={{ color: '#1B2430' }}>Extend impact measurement as your program scales.</strong> Additional pre- and post-program measurement can be purchased when the participant population exceeds the allowance included in the selected validation tier.
                            </p>
                        </div>
                    </div>
                </section>

                {/* ── SECTION 3: IMPACT MEASUREMENT AND LIFECYCLE ──────────── */}
                <section aria-label="Impact measurement and lifecycle" className={classes.impactSection}>
                    <div className={classes.sectionContainer}>
                        <div className={classes.impactGrid}>
                            <div className={classes.impactCard}>
                                <span className={classes.sectionEyebrow}>Impact measurement</span>
                                <h2 className={classes.cardTitle}>Show whether participant capability changes.</h2>
                                <p className={classes.cardLead}>
                                    Standard and Comprehensive connect the program’s intended outcomes to a tailored pre- and post-assessment.
                                </p>
                                <ol className={classes.impactStepsList}>
                                    {IMPACT_STEPS.map((step) => (
                                        <li key={step.num} className={classes.impactStepItem}>
                                            <span className={classes.impactStepNum}>{step.num}</span>
                                            <span>{step.text}</span>
                                        </li>
                                    ))}
                                </ol>
                                <p className={classes.impactFootnote}>
                                    Standard includes up to 100 participants. Comprehensive includes up to 1,000 participants. Additional participant measurement can be added according to annual volume.
                                </p>
                            </div>

                            <div className={classes.lifecycleCard}>
                                <span className={classes.sectionEyebrow}>Validation lifecycle</span>
                                <h2 className={classes.cardTitle}>Maintain confidence as your program evolves.</h2>
                                <div className={classes.lifecycleWrap}>
                                    {LIFECYCLE_STEPS.map((step) => (
                                        <div key={step.num} className={classes.lifecycleRow}>
                                            <span className={classes.lifecycleNum}>{step.num}</span>
                                            <span className={classes.lifecycleText}>{step.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── SECTION 4: COMMERCIAL SCOPE ──────────────────────────── */}
                <section id="quotation" aria-label="Commercial scope" className={classes.quotationSection}>
                    <div className={classes.sectionContainer}>
                        <div className={classes.quotationPanel}>
                            <div className={classes.quotationLeft}>
                                <span className={classes.quotationPill}>Commercial scope</span>
                                <h2 className={classes.quotationTitle}>
                                    Receive a quotation matched to your program.
                                </h2>
                                <p className={classes.quotationLead}>
                                    P3 is available through an approved DQ Pro or directly from Powered by DQ. Your quotation reflects:
                                </p>
                                <ul className={classes.quotationList}>
                                    {SCOPE_POINTS.map((point) => (
                                        <li key={point} className={classes.quotationItem}>
                                            <CheckIcon color="#ED6628" />
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className={classes.quotationFootnote}>
                                    The fee covers the independent review and agreed measurement work. It does not guarantee a particular validation outcome.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={openAgentChat}
                                className={classes.btnQuotation}
                            >
                                Request a scoped quotation
                                <PbdqArrowRightIcon />
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            {/* ── SECTION 5: NEXT STEP ────────────────────────────────────── */}
            <section aria-label="Next step" className={classes.nextStepSection}>
                <div className={classes.nextStepContainer}>
                    <h2 className={classes.nextStepTitle}>
                        Independent evidence, from alignment to impact.
                    </h2>
                    <div className={classes.nextStepCtas}>
                        <a
                            href="#tiers"
                            onClick={scrollToTiers}
                            className={classes.btnBottomDark}
                        >
                            Choose your validation tier
                        </a>
                        <button
                            type="button"
                            onClick={openAgentChat}
                            className={classes.btnBottomWhite}
                        >
                            Request a scoped quotation
                        </button>
                    </div>
                </div>
            </section>

            <PbdqFooter />
        </div>
    );
};

export default PbdqProgramValidationPageContent;
